import { Id } from 'types/definitions'
import { IndividualProfileAPI } from './API'
import {
  DependentProfileVersionDto,
  PersonalProfileVersionDto,
  SpousalProfileVersionDto,
} from './dtos'
import { PersonalProfileVersion } from './PersonalProfile/PersonalProfileVersion'
import { collection } from 'infra/collection'
import { SpousalProfileVersion } from './SpousalProfile/SpousalProfileVersion'
import { DependentProfileVersion } from './DependentProfile/DependentProfileVersion'

export default class IndividualProfileStore {
  personalProfiles = collection<
    PersonalProfileVersionDto,
    PersonalProfileVersion
  >({
    create: () => new PersonalProfileVersion(),
    getDataId: (data) => `${data.id}:${data.version}`,
    getModelId: (model) => `${model.id}:${model.version}`,
  })

  spousalProfiles = collection<SpousalProfileVersionDto, SpousalProfileVersion>(
    {
      create: () => new SpousalProfileVersion(),
      getDataId: (data) => `${data.id}:${data.version}`,
      getModelId: (model) => `${model.id}:${model.version}`,
    },
  )

  dependentProfiles = collection<
    DependentProfileVersionDto,
    DependentProfileVersion
  >({
    create: () => new DependentProfileVersion(),
    getDataId: (data) => `${data.id}:${data.version}`,
    getModelId: (model) => `${model.id}:${model.version}`,
  })

  constructor(private api: IndividualProfileAPI) {}

  /**
   * Gets personal profile.
   */
  getPersonalProfile(id: Id, version: number) {
    return this.personalProfiles.get(`${id}:${version}`)
  }

  /**
   * Gets spousal profile.
   */
  getSpousalProfile(id: Id, version: number) {
    return this.spousalProfiles.get(`${id}:${version}`)
  }

  /**
   * Gets dependent profile.
   */
  getDependentProfile(id: Id, version: number) {
    return this.dependentProfiles.get(`${id}:${version}`)
  }

  /**
   * Fetches personal profile for a given job ID.
   */
  async fetchPersonalProfileForJob(jobId: Id) {
    return this.api
      .getPersonalProfileForJob(jobId)
      .then(this.personalProfiles.set)
  }

  /**
   * Fetches personal profile.
   */
  async fetchPersonalProfile(id: Id) {
    return this.api.getPersonalProfile(id).then(this.personalProfiles.set)
  }

  /**
   * Fetches spousal profile for a given job ID.
   */
  async fetchSpousalProfileForJob(jobId: Id) {
    return this.api
      .getSpousalProfileForJob(jobId)
      .then(this.spousalProfiles.set)
  }

  /**
   * Fetches spousal profile.
   */
  async fetchSpousalProfile(id: Id) {
    return this.api.getSpousalProfile(id).then((profile) => {
      return this.spousalProfiles.set(profile)
    })
  }

  /**
   * Fetches a dependent profile for a given job ID.
   */
  async fetchDependentProfileForJob(id: Id, jobId: Id) {
    return this.api
      .getDependentProfileForJob(id, jobId)
      .then(this.dependentProfiles.set)
  }

  /**
   * Fetches a dependents profile.
   */
  async fetchDependentProfile(id: Id) {
    return this.api.getDependentProfile(id).then((profile) => {
      return this.dependentProfiles.set(profile)
    })
  }
}
